import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { GET_COST_DASHBOARD, LIST_COST_EVENTS } from '../graphql/operations';
import authService from '../auth/authService';
import './AdminShared.css';
import './CostDashboard.css';

const RANGE_OPTIONS = [
  { value: '24', label: 'Last 24 hours' },
  { value: '72', label: 'Last 72 hours' },
  { value: '168', label: 'Last 7 days' }
];

const GRANULARITY_OPTIONS = [
  { value: 'HOUR', label: 'Hourly buckets' },
  { value: 'DAY', label: 'Daily buckets' }
];

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'complete', label: 'Complete' },
  { value: 'success', label: 'Success' },
  { value: 'canceled', label: 'Canceled' },
  { value: 'error', label: 'Error' },
  { value: 'streaming_queued', label: 'Streaming queued' },
  { value: 'agent_disabled', label: 'Agent disabled' }
];

const QUICK_STATUS_CHIPS = [
  { value: '', label: 'All' },
  { value: 'error', label: 'Error' },
  { value: 'canceled', label: 'Canceled' },
  { value: 'success', label: 'Success' }
];

function formatCurrency(value) {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(0);
  }

  if (numeric === 0) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(0);
  }

  const absolute = Math.abs(numeric);
  if (absolute >= 0.01) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numeric);
  }

  // Costs are persisted at micro-dollar precision (6 decimals). Show enough decimals
  // to reveal the first non-zero fractional digit for tiny values.
  const fixed = absolute.toFixed(6);
  const fractional = fixed.split('.')[1] || '';
  const trimmedFractional = fractional.replace(/0+$/, '');
  const dynamicDecimals = Math.max(2, Math.min(6, trimmedFractional.length || 2));

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: dynamicDecimals,
    maximumFractionDigits: dynamicDecimals
  }).format(numeric);
}

function formatInt(value) {
  return new Intl.NumberFormat('en-US').format(Number(value || 0));
}

function formatPercent(value) {
  return `${(Number(value || 0) * 100).toFixed(1)}%`;
}

function formatTimestamp(value) {
  if (!value) return 'N/A';
  const date = new Date(value);
  return date.toLocaleString();
}

function relativeTime(timestamp) {
  if (!timestamp) return '';
  const now = Date.now();
  const then = new Date(timestamp).getTime();
  const diffMs = now - then;
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(timestamp).toLocaleDateString();
}

function statusColor(status) {
  if (status === 'success') return 'green';
  if (status === 'error' || status === 'critical') return 'red';
  return 'amber';
}

function alertTone(level) {
  if (level === 'critical') return 'critical';
  if (level === 'warning') return 'warning';
  return 'ok';
}

function formatBucketLabel(value) {
  if (!value) return 'N/A';
  return new Date(value).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit'
  });
}

function formatDeltaPercent(delta) {
  if (delta === null || Number.isNaN(delta)) return 'new';
  const signed = delta > 0 ? '+' : '';
  return `${signed}${(delta * 100).toFixed(0)}%`;
}

function compareDelta(currentValue, previousValue) {
  if (Number(previousValue) <= 0) return null;
  return (Number(currentValue) - Number(previousValue)) / Number(previousValue);
}

function statusLabel(status) {
  if (!status) return 'unknown';
  return status.replaceAll('_', ' ');
}

function statusWeight(status) {
  if (status === 'error') return 4;
  if (status === 'canceled' || status === 'streaming_queued' || status === 'agent_disabled') return 3;
  if (status === 'success') return 1;
  return 2;
}

function formatDurationMs(value) {
  const duration = Number(value || 0);
  if (!duration || duration < 1000) return `${Math.round(duration)}ms`;
  const seconds = duration / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ${(seconds % 60).toFixed(0)}s`;
}

function shortenId(value, size = 8) {
  if (!value) return '';
  return value.length > size ? value.slice(0, size) : value;
}

function CostDashboard() {
  const isAdmin = authService.hasRole('admin') || authService.hasRole('system_admin');
  const eventsFeedRef = useRef(null);
  const [rangeHours, setRangeHours] = useState('24');
  const [granularity, setGranularity] = useState('HOUR');
  const [environment, setEnvironment] = useState('');
  const [service, setService] = useState('');
  const [feature, setFeature] = useState('');
  const [model, setModel] = useState('');
  const [status, setStatus] = useState('');
  const [username, setUsername] = useState('');
  const [conversationId, setConversationId] = useState('');
  const [eventItems, setEventItems] = useState([]);
  const [eventCursor, setEventCursor] = useState(null);
  const [isFilterTransitioning, setIsFilterTransitioning] = useState(false);

  const range = useMemo(() => {
    const end = new Date();
    const start = new Date(end.getTime() - Number(rangeHours) * 60 * 60 * 1000);
    return {
      start: start.toISOString(),
      end: end.toISOString(),
      granularity
    };
  }, [granularity, rangeHours]);

  const dashboardFilter = useMemo(() => {
    const nextFilter = {};
    if (environment.trim()) nextFilter.environment = environment.trim();
    if (service.trim()) nextFilter.service = service.trim();
    if (feature.trim()) nextFilter.feature = feature.trim();
    if (model.trim()) nextFilter.model = model.trim();
    return nextFilter;
  }, [environment, feature, model, service]);

  const eventFilter = useMemo(() => {
    const nextFilter = {
      ...dashboardFilter,
      startDate: range.start,
      endDate: range.end
    };
    if (status.trim()) nextFilter.status = status.trim();
    if (username.trim()) nextFilter.username = username.trim();
    if (conversationId.trim()) nextFilter.conversationId = conversationId.trim();
    return nextFilter;
  }, [conversationId, dashboardFilter, range.end, range.start, status, username]);

  useEffect(() => {
    setIsFilterTransitioning(true);
    const timer = window.setTimeout(() => setIsFilterTransitioning(false), 280);
    return () => window.clearTimeout(timer);
  }, [rangeHours, granularity, environment, service, feature, model, status, username, conversationId]);

  const {
    data: dashboardData,
    loading: dashboardLoading,
    error: dashboardError
  } = useQuery(GET_COST_DASHBOARD, {
    variables: {
      range,
      filter: Object.keys(dashboardFilter).length > 0 ? dashboardFilter : undefined
    },
    skip: !isAdmin,
    fetchPolicy: 'network-only'
  });

  const {
    data: eventsData,
    loading: eventsLoading,
    error: eventsError,
    fetchMore: fetchMoreEvents
  } = useQuery(LIST_COST_EVENTS, {
    variables: {
      filter: eventFilter,
      limit: 25
    },
    skip: !isAdmin,
    fetchPolicy: 'network-only',
    onCompleted: (result) => {
      setEventItems(result?.listCostEvents?.items || []);
      setEventCursor(result?.listCostEvents?.nextCursor || null);
    }
  });

  const dashboard = dashboardData?.getCostDashboard;
  const totals = dashboard?.totals;
  const trend = useMemo(() => dashboard?.trend || [], [dashboard]);
  const byService = dashboard?.byService || [];
  const byFeature = dashboard?.byFeature || [];
  const byModel = useMemo(() => dashboard?.byModel || [], [dashboard]);
  const alertState = dashboard?.alertState;

  const maxTrendCost = trend.reduce((max, point) => Math.max(max, Number(point.estimatedCostUsd || 0)), 0) || 1;
  const topServices = byService.slice(0, 5);
  const topFeatures = byFeature.slice(0, 5);
  const topModels = byModel.slice(0, 5);
  const selectedRangeLabel = RANGE_OPTIONS.find((option) => option.value === rangeHours)?.label || 'Last 24 hours';

  const errorRate = totals?.requestCount
    ? totals.errorCount / Math.max(1, totals.requestCount)
    : 0;
  const retryPressure = Number(totals?.errorCount || 0) + Number(totals?.canceledCount || 0);

  const displayedEventItems = useMemo(() => (
    eventItems.length > 0
      ? eventItems
      : (eventsData?.listCostEvents?.items || [])
  ), [eventItems, eventsData]);
  const activeCursor = eventCursor || eventsData?.listCostEvents?.nextCursor || null;
  const allKnownModelsWithEvents = [...new Set([...byModel.map((row) => row.key), ...displayedEventItems.map((row) => row.model)].filter(Boolean))];
  const statusOptions = useMemo(() => {
    const dynamicStatuses = [...new Set(displayedEventItems.map((row) => row.status).filter(Boolean))]
      .filter((value) => !STATUS_OPTIONS.some((option) => option.value === value))
      .map((value) => ({
        value,
        label: statusLabel(value).replace(/\b\w/g, (letter) => letter.toUpperCase())
      }));
    return [...STATUS_OPTIONS, ...dynamicStatuses];
  }, [displayedEventItems]);
  const allKnownEnvironments = useMemo(() => {
    const defaults = ['dev', 'prod'];
    const dynamicEnvironments = displayedEventItems.map((row) => row.environment).filter(Boolean);
    return [...new Set([...defaults, ...dynamicEnvironments])];
  }, [displayedEventItems]);
  const activeFilterCount = useMemo(
    () => [environment, service, feature, model, status, username, conversationId].filter((value) => Boolean(value && value.trim())).length,
    [environment, service, feature, model, status, username, conversationId]
  );

  const rankedTrend = useMemo(() => {
    const trendWithDeltas = trend.map((point, index) => {
      const previousPoint = trend[index - 1];
      const currentCost = Number(point.estimatedCostUsd || 0);
      const previousCost = Number(previousPoint?.estimatedCostUsd || 0);
      return {
        ...point,
        currentCost,
        deltaFromPrevious: previousPoint ? compareDelta(currentCost, previousCost) : null
      };
    });

    return [...trendWithDeltas]
      .sort((a, b) => b.currentCost - a.currentCost)
      .slice(0, 4);
  }, [trend]);

  const topStableModel = useMemo(() => {
    const candidates = byModel
      .filter((row) => Number(row.requestCount || 0) > 0)
      .map((row) => ({
        ...row,
        errorRate: Number(row.errorCount || 0) / Math.max(1, Number(row.requestCount || 0))
      }))
      .sort((a, b) => a.errorRate - b.errorRate || Number(b.requestCount || 0) - Number(a.requestCount || 0));

    return candidates[0] || null;
  }, [byModel]);

  const triageQueue = useMemo(() => {
    return [...displayedEventItems]
      .sort((left, right) => {
        const leftScore =
          (statusWeight(left.status) * 100) +
          (Number(left.estimatedCostUsd || 0) * 1000) +
          (Number(left.totalTokens || 0) / 1000);
        const rightScore =
          (statusWeight(right.status) * 100) +
          (Number(right.estimatedCostUsd || 0) * 1000) +
          (Number(right.totalTokens || 0) / 1000);
        return rightScore - leftScore;
      })
      .slice(0, 3);
  }, [displayedEventItems]);

  const contributorGroups = useMemo(
    () => ([
      { key: 'service', title: 'By Service', rows: topServices },
      { key: 'feature', title: 'By Feature', rows: topFeatures },
      { key: 'model', title: 'By Model', rows: topModels }
    ]),
    [topFeatures, topModels, topServices]
  );

  const alertLevel = alertTone(alertState?.level);
  const anomalyCards = useMemo(() => {
    const topFeature = topFeatures[0];
    const topService = topServices[0];
    const alertToneVariant = alertLevel === 'critical'
      ? 'critical'
      : (alertLevel === 'warning' ? 'warning' : 'recovered');

    return [
      {
        id: 'primary',
        tone: alertToneVariant,
        badge: alertLevel === 'critical' ? 'Critical' : (alertLevel === 'warning' ? 'Warning' : 'Recovered'),
        title: alertState?.message || 'No active runtime anomalies',
        detail: alertState?.bucketStart
          ? `${formatBucketLabel(alertState.bucketStart)} bucket • ${formatCurrency(alertState.estimatedCostUsd)}`
          : selectedRangeLabel
      },
      {
        id: 'driver',
        tone: 'warning',
        badge: 'Spend Driver',
        title: topFeature?.key
          ? `${topFeature.key} is leading spend in this window`
          : 'No single feature dominates spend',
        detail: topFeature?.key
          ? `${formatCurrency(topFeature.estimatedCostUsd)} • ${topService?.key || 'all services'}`
          : 'Adjust filters for spend attribution'
      },
      {
        id: 'stability',
        tone: 'recovered',
        badge: 'Recovered',
        title: topStableModel?.key
          ? `${topStableModel.key} shows strongest stability`
          : 'Stability signal unavailable in this range',
        detail: topStableModel?.key
          ? `${formatPercent(topStableModel.errorRate)} error rate • ${formatInt(topStableModel.requestCount)} requests`
          : 'Try a longer window for model-level stability'
      }
    ];
  }, [alertLevel, alertState, selectedRangeLabel, topFeatures, topServices, topStableModel]);

  const handleLoadMoreEvents = async () => {
    if (!activeCursor) return;
    const result = await fetchMoreEvents({
      variables: {
        filter: eventFilter,
        limit: 25,
        cursor: activeCursor
      }
    });

    const nextItems = result?.data?.listCostEvents?.items || [];
    setEventItems((previous) => [...previous, ...nextItems]);
    setEventCursor(result?.data?.listCostEvents?.nextCursor || null);
  };

  const handleInvestigateEvent = (item) => {
    setService(item?.service || '');
    setFeature(item?.feature || '');
    setModel(item?.model || '');
    setStatus(item?.status || '');
    setUsername(item?.username || '');
    setConversationId(item?.conversationId || '');
    window.requestAnimationFrame(() => {
      eventsFeedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const handleFocusModel = (item) => {
    setModel(item?.model || '');
    setStatus(item?.status || '');
    window.requestAnimationFrame(() => {
      eventsFeedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const handleClearFilters = () => {
    setEnvironment('');
    setService('');
    setFeature('');
    setModel('');
    setStatus('');
    setUsername('');
    setConversationId('');
  };

  if (!isAdmin) {
    return <div className="cd-state">Access denied. Admin role required.</div>;
  }

  if (dashboardLoading && !dashboard) {
    return <div className="cd-state">Loading cost telemetry...</div>;
  }

  if (dashboardError && !dashboard) {
    return <div className="cd-state cd-state-error">Unable to load dashboard: {dashboardError.message}</div>;
  }

  return (
    <section className="cd">
      <header className="admin-view-header cd-header">
        <div>
          <h2>Cost Dashboard</h2>
          <p>LLM runtime cost telemetry and usage analytics.</p>
        </div>
        <div className={`cd-alert cd-alert-${alertLevel}`} role="status" aria-live="polite">
          <span className="cd-alert-dot" aria-hidden="true" />
          <div>
            <strong>{alertState?.message || 'All systems operational'}</strong>
            <p>Range: {formatTimestamp(range.start)} to {formatTimestamp(range.end)}</p>
          </div>
        </div>
      </header>

      <div className={`admin-filters cd-filters ${isFilterTransitioning ? 'is-transitioning' : ''}`}>
        <div className="cd-filter-toolbar">
          <div className="cd-filter-summary">
            <span className="admin-pill admin-pill-sm admin-pill-gray">
              {activeFilterCount} active filters
            </span>
            <span className="cd-filter-summary-text">Use quick status chips or clear to reset.</span>
          </div>
          <div className="cd-filter-actions">
            <div className="cd-status-chips" role="group" aria-label="Quick status filters">
              {QUICK_STATUS_CHIPS.map((chip) => (
                <button
                  key={chip.label}
                  type="button"
                  className={`cd-status-chip ${status === chip.value ? 'is-active' : ''}`}
                  onClick={() => setStatus(chip.value)}
                >
                  {chip.label}
                </button>
              ))}
            </div>
            <button type="button" className="cd-clear-btn" onClick={handleClearFilters}>
              Clear filters
            </button>
          </div>
        </div>
        <label className="admin-filter-field">
          <span className="admin-filter-label">Environment</span>
          <select className="admin-filter-input" value={environment} onChange={(event) => setEnvironment(event.target.value)}>
            <option value="">Current environment</option>
            {allKnownEnvironments.map((environmentName) => (
              <option key={environmentName} value={environmentName}>{environmentName}</option>
            ))}
          </select>
        </label>
        <label className="admin-filter-field">
          <span className="admin-filter-label">Window</span>
          <select className="admin-filter-input" value={rangeHours} onChange={(event) => setRangeHours(event.target.value)}>
            {RANGE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
        <label className="admin-filter-field">
          <span className="admin-filter-label">Granularity</span>
          <select className="admin-filter-input" value={granularity} onChange={(event) => setGranularity(event.target.value)}>
            {GRANULARITY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
        <label className="admin-filter-field">
          <span className="admin-filter-label">Service</span>
          <input className="admin-filter-input" value={service} onChange={(event) => setService(event.target.value)} placeholder="openai" />
        </label>
        <label className="admin-filter-field">
          <span className="admin-filter-label">Feature</span>
          <input className="admin-filter-input" value={feature} onChange={(event) => setFeature(event.target.value)} placeholder="chat" />
        </label>
        <label className="admin-filter-field">
          <span className="admin-filter-label">Model</span>
          <input className="admin-filter-input" list="cost-models" value={model} onChange={(event) => setModel(event.target.value)} placeholder="gpt-5.4" />
          <datalist id="cost-models">
            {allKnownModelsWithEvents.map((modelOption) => (
              <option key={modelOption} value={modelOption} />
            ))}
          </datalist>
        </label>
        <label className="admin-filter-field">
          <span className="admin-filter-label">Status</span>
          <select className="admin-filter-input" value={status} onChange={(event) => setStatus(event.target.value)}>
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
        <label className="admin-filter-field">
          <span className="admin-filter-label">Username</span>
          <input className="admin-filter-input" value={username} onChange={(event) => setUsername(event.target.value)} placeholder="jane@kapsa.ai" />
        </label>
        <label className="admin-filter-field">
          <span className="admin-filter-label">Conversation ID</span>
          <input className="admin-filter-input" value={conversationId} onChange={(event) => setConversationId(event.target.value)} placeholder="optional" />
        </label>
      </div>

      <section className="cd-anomaly-rail" aria-label="Signal-first anomaly rail">
        {anomalyCards.map((card) => (
          <article key={card.id} className={`cd-anomaly cd-anomaly-${card.tone}`}>
            <div className="cd-anomaly-header">
              <span className="admin-pill admin-pill-sm admin-pill-gray">{card.badge}</span>
            </div>
            <h3>{card.title}</h3>
            <p>{card.detail}</p>
          </article>
        ))}
      </section>

      <section className="cd-metrics" aria-label="Cost summary cards">
        <article className="cd-metric cd-metric-spend">
          <div className="cd-metric-header">
            <span className="cd-metric-label">Estimated Spend</span>
            <span className="admin-pill admin-pill-sm admin-pill-blue">{selectedRangeLabel}</span>
          </div>
          <div className="cd-metric-value">{formatCurrency(totals?.estimatedCostUsd)}</div>
          <div className="cd-metric-trend">
            <span className="cd-trend-label">{formatInt(totals?.requestCount)} requests</span>
          </div>
        </article>

        <article className="cd-metric cd-metric-error">
          <div className="cd-metric-header">
            <span className="cd-metric-label">Error Rate</span>
            <span className="admin-pill admin-pill-sm admin-pill-amber">Risk</span>
          </div>
          <div className="cd-metric-value">{formatPercent(errorRate)}</div>
          <div className="cd-metric-trend">
            <span className={errorRate > 0 ? 'cd-trend-negative' : 'cd-trend-positive'}>
              {formatInt(totals?.errorCount)} errors
            </span>
            <span className="cd-trend-label">{formatInt(totals?.canceledCount)} canceled</span>
          </div>
        </article>

        <article className="cd-metric cd-metric-tokens">
          <div className="cd-metric-header">
            <span className="cd-metric-label">Total Tokens</span>
            <span className="admin-pill admin-pill-sm admin-pill-green">Usage</span>
          </div>
          <div className="cd-metric-value">{formatInt(totals?.totalTokens)}</div>
          <div className="cd-metric-trend">
            <span className="cd-trend-positive">{formatInt(totals?.partialPricingCount)} partial-pricing windows</span>
          </div>
        </article>

        <article className="cd-metric cd-metric-retry">
          <div className="cd-metric-header">
            <span className="cd-metric-label">Retry Pressure</span>
            <span className="admin-pill admin-pill-sm admin-pill-red">Triage</span>
          </div>
          <div className="cd-metric-value">{formatInt(retryPressure)}</div>
          <div className="cd-metric-trend">
            <span className={retryPressure > 0 ? 'cd-trend-negative' : 'cd-trend-positive'}>
              {retryPressure > 0 ? 'Potential incident precursor' : 'No retry pressure'}
            </span>
          </div>
        </article>
      </section>

      <section className="cd-panels">
        <article className="cd-panel">
          <header>
            <h3>Top Cost Spikes + Comparison</h3>
            <p>Ranked by spend and compared to previous bucket.</p>
          </header>
          <div className="cd-bar-list" role="list">
            {rankedTrend.length === 0 ? (
              <div className="cd-empty">No cost trend data in this range.</div>
            ) : (
              rankedTrend.map((point) => (
                <div className="cd-bar-row" key={point.bucketStart} role="listitem">
                  <time title={formatTimestamp(point.bucketStart)}>{formatBucketLabel(point.bucketStart)}</time>
                  <div className="cd-bar-track" aria-hidden="true">
                    <div
                      className="cd-bar-fill"
                      style={{ width: `${Math.max(4, (Number(point.estimatedCostUsd || 0) / maxTrendCost) * 100)}%` }}
                    />
                  </div>
                  <div className="cd-bar-values">
                    <strong>{formatCurrency(point.estimatedCostUsd)}</strong>
                    <span className={`cd-bar-delta ${
                      point.deltaFromPrevious > 0
                        ? 'cd-bar-delta-up'
                        : (point.deltaFromPrevious < 0 ? 'cd-bar-delta-down' : 'cd-bar-delta-flat')
                    }`}
                    >
                      {formatDeltaPercent(point.deltaFromPrevious)}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="cd-panel-note">
            <strong>Insight:</strong>{' '}
            {rankedTrend[0]
              ? `${formatBucketLabel(rankedTrend[0].bucketStart)} is the top spend bucket in ${selectedRangeLabel.toLowerCase()}.`
              : 'Adjust filters to recover cost spike insights.'}
          </div>
        </article>

        <article className="cd-panel">
          <header>
            <h3>Triage Queue</h3>
            <p>Highest impact events ranked by severity, cost, and volume.</p>
          </header>
          {triageQueue.length === 0 ? (
            <div className="cd-empty">No events available for triage in this range.</div>
          ) : (
            <div className="cd-triage-list">
              {triageQueue.map((item) => (
                <article key={item.eventId} className="cd-incident-card">
                  <div className="cd-incident-head">
                    <span className={`admin-pill admin-pill-sm admin-pill-${statusColor(item.status)}`}>
                      {statusLabel(item.status)}
                    </span>
                    <strong>{formatCurrency(item.estimatedCostUsd)}</strong>
                  </div>
                  <h4>{item.service}/{item.feature}</h4>
                  <p>{item.model || 'unknown'} · {item.username || item.userId || 'unknown'} · {formatInt(item.totalTokens)} tok</p>
                  <div className="cd-incident-actions">
                    <button type="button" className="cd-incident-btn cd-incident-btn-primary" onClick={() => handleInvestigateEvent(item)}>
                      Investigate
                    </button>
                    <button type="button" className="cd-incident-btn cd-incident-btn-secondary" onClick={() => handleFocusModel(item)}>
                      Focus model
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </article>
      </section>

      <section className="cd-panel cd-contributors" aria-label="Top cost contributors">
        <header>
          <h3>Top Contributors</h3>
          <p>Highest spend across service, feature, and model dimensions.</p>
        </header>
        <div className="cd-contributor-row">
          {contributorGroups.map((group) => {
            const maxGroupCost = group.rows.reduce((maxCost, row) => Math.max(maxCost, Number(row.estimatedCostUsd || 0)), 0) || 1;
            return (
              <article className="cd-contributor-card" key={group.key}>
                <div className="cd-contributor-head">
                  <h4>{group.title}</h4>
                  <span className="admin-pill admin-pill-sm admin-pill-gray">{group.rows.length} items</span>
                </div>
                {group.rows.length === 0 ? (
                  <div className="cd-empty">No contributor data for this range.</div>
                ) : (
                  <ul>
                    {group.rows.slice(0, 4).map((row) => {
                      const rowErrorRate = Number(row.requestCount || 0) > 0
                        ? Number(row.errorCount || 0) / Number(row.requestCount || 0)
                        : 0;
                      return (
                        <li key={`${group.key}-${row.key}`}>
                          <div className="cd-contributor-item-top">
                            <span>{row.key}</span>
                            <strong>{formatCurrency(row.estimatedCostUsd)}</strong>
                          </div>
                          <div className="cd-contributor-track" aria-hidden="true">
                            <div className="cd-contributor-fill" style={{ width: `${Math.max(6, (Number(row.estimatedCostUsd || 0) / maxGroupCost) * 100)}%` }} />
                          </div>
                          <div className="cd-contributor-meta">
                            <span>{formatInt(row.requestCount)} req</span>
                            <span>{formatPercent(rowErrorRate)} err</span>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </article>
            );
          })}
        </div>
      </section>

      <section className="cd-events" ref={eventsFeedRef} aria-label="Raw cost events">
        <header>
          <h3>Raw Events Feed</h3>
          <p>Detailed event stream to investigate spend spikes and failures.</p>
        </header>

        {eventsError && eventItems.length === 0 ? (
          <div className="cd-empty">Unable to load cost events: {eventsError.message}</div>
        ) : (
          <>
            {displayedEventItems.length === 0 ? (
              <div className="cd-empty">No events for current filters.</div>
            ) : (
              <div className="admin-card-list cd-event-list">
                {displayedEventItems.map((item) => (
                  <div className="admin-card-row cd-event-row" key={item.eventId}>
                    <div className="cd-event-leading">
                      <span className={`admin-pill admin-pill-sm admin-pill-${statusColor(item.status)}`}>
                        {statusLabel(item.status)}
                      </span>
                    </div>
                    <div className="cd-event-main">
                      <div className="cd-event-primary">{item.service}/{item.feature}</div>
                      <div className="cd-event-secondary">{item.model || 'unknown'} · {item.username || item.userId || 'unknown'}</div>
                      <div className="cd-event-badges">
                        <span className="admin-pill admin-pill-sm admin-pill-gray">{item.operation || 'unknown_op'}</span>
                        <span className={`admin-pill admin-pill-sm admin-pill-${item.pricingStatus === 'actual' ? 'green' : 'amber'}`}>
                          {item.pricingStatus || 'estimated'}
                        </span>
                        <span className="admin-pill admin-pill-sm admin-pill-blue">{item.environment || 'n/a'}</span>
                        {item.conversationId && (
                          <span className="admin-pill admin-pill-sm admin-pill-purple">conv {shortenId(item.conversationId)}</span>
                        )}
                      </div>
                    </div>
                    <div className="cd-event-metrics">
                      <div className="cd-event-metric">
                        <span>Cost</span>
                        <strong>{formatCurrency(item.estimatedCostUsd)}</strong>
                      </div>
                      <div className="cd-event-metric">
                        <span>Tokens</span>
                        <strong>{formatInt(item.totalTokens)}</strong>
                      </div>
                      <div className="cd-event-metric">
                        <span>Latency</span>
                        <strong>{formatDurationMs(item.durationMs)}</strong>
                      </div>
                    </div>
                    <div className="cd-event-actions">
                      <button type="button" className="cd-incident-btn cd-incident-btn-secondary" onClick={() => handleInvestigateEvent(item)}>
                        Investigate
                      </button>
                    </div>
                    <div className="cd-event-time" title={formatTimestamp(item.createdAt)}>{relativeTime(item.createdAt)}</div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {activeCursor && (
          <div className="admin-load-more">
            <button type="button" className="admin-load-more-btn" onClick={handleLoadMoreEvents} disabled={eventsLoading}>
              {eventsLoading ? 'Loading...' : 'Load more events'}
            </button>
          </div>
        )}
      </section>
    </section>
  );
}

export default CostDashboard;
